import React, { FC } from 'react';
import { Formik, FormikProps, Form } from 'formik';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { Grid, TextField, Button, makeStyles, FormControlLabel, Checkbox } from '@material-ui/core';
import * as Yup from 'yup';
import type { TransferInput } from './types';
import { Partner } from '../HotelForm/types';
import AutoSuggest from '../../../Bookings/BookingForm/AutoSuggest';

const transferSchema = Yup.object().shape({
  company: Yup.string().required('Required'),
  dates: Yup.array().min(1, '')
});

interface Props {
  initialValues?: TransferInput;
  onSubmit: (values: TransferInput) => void;
  onSuccess?: () => void;
  partnerOptions: Array<{ label: string; value: Partner }>;
  submitLabel?: string;
}

const useStyles = makeStyles((theme) => ({
  input: {
    width: '100%'
  },
  button: {
    marginTop: theme.spacing(2)
  }
}));

const TransferForm: FC<Props> = ({
  initialValues = { dates: [], company: '', partner: null },
  partnerOptions,
  submitLabel = 'Add transfer',
  onSubmit,
  onSuccess
}) => {
  const { input, button } = useStyles();
  const [roundTrip, setRoundTrip] = React.useState(initialValues?.dates.length > 1);

  const handleSubmit = (values: TransferInput) => {
    const { id, ...rest } = values;
    onSubmit(rest);
    onSuccess?.();
  };

  return (
    <Formik enableReinitialize={true} initialValues={initialValues} onSubmit={handleSubmit} validationSchema={transferSchema}>
      {({ errors, touched, handleChange, handleBlur, values, setFieldValue }: FormikProps<TransferInput>) => (
        <Form>
          {console.log({ values })}
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  id="outlined-name"
                  label="Company"
                  name="company"
                  value={values.company}
                  variant="outlined"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.company && !!touched.company}
                  helperText={!!errors.company && !!touched.company && errors.company}
                  margin="normal"
                  className={input}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <KeyboardDatePicker
                  format="dd/MM/yyyy"
                  onFocus={() => {}}
                  margin="normal"
                  id="date-picker-inline"
                  label={values.dates.length > 1 ? 'Pick up date' : 'Date'}
                  KeyboardButtonProps={{
                    'aria-label': 'change date'
                  }}
                  name="dates"
                  inputVariant="outlined"
                  value={values.dates[0]}
                  onChange={(date) => {
                    if (date) {
                      const dates = [...values.dates];
                      dates[0] = date;

                      setFieldValue('dates', dates);
                    } else {
                      setFieldValue('dates', date);
                    }
                  }}
                  onBlur={handleBlur}
                  error={!!errors.dates && !!touched.dates}
                  helperText={!!errors.dates && !!touched.dates && errors.dates}
                  className={input}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <KeyboardDatePicker
                  format="dd/MM/yyyy"
                  onFocus={() => {}}
                  margin="normal"
                  id="date-picker-inline"
                  disabled={!roundTrip}
                  label="Drop off date"
                  KeyboardButtonProps={{
                    'aria-label': 'change date'
                  }}
                  name="dates"
                  inputVariant="outlined"
                  value={values.dates[1]}
                  onChange={(date) => {
                    if (date) {
                      const dates = [...values.dates];
                      dates[1] = date;

                      setFieldValue('dates', dates);
                    } else {
                      const dates = [...values.dates];
                      dates.pop();
                      setFieldValue('dates', dates);
                    }
                  }}
                  onBlur={handleBlur}
                  error={!!errors.dates && !!touched.dates}
                  helperText={!!errors.dates && !!touched.dates && errors.dates}
                  className={input}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      value={roundTrip}
                      onChange={() => {
                        setRoundTrip((value) => {
                          const newValue = !value;
                          if (!newValue) {
                            const dates = [...values.dates];
                            dates.pop();
                            setFieldValue('dates', dates);
                          }

                          return newValue;
                        });
                      }}
                    />
                  }
                  label="Roundtrip"
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <AutoSuggest
                  label="Partner agency"
                  error={!!errors.partner && !!touched.partner}
                  placeholder="Search for partner"
                  value={values.partner}
                  name="partner"
                  onChange={(value) => setFieldValue('partner', value)}
                  suggestions={partnerOptions}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  id="outlined-name"
                  label="Net total"
                  name="total"
                  value={values.total}
                  onChange={handleChange}
                  variant="outlined"
                  onBlur={handleBlur}
                  error={!!errors.total && !!touched.total}
                  helperText={!!errors.total && !!touched.total && errors.total}
                  margin="normal"
                  className={input}
                  type="number"
                  onWheel={(e) => {
                    // Prevent the input value change
                    (e.target as HTMLInputElement).blur();;
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="outlined-multiline-static"
                  variant="outlined"
                  label="Notes"
                  name="notes"
                  value={values.notes}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.notes && !!touched.notes}
                  helperText={!!errors.notes && !!touched.notes && errors.notes}
                  style={{ width: '100%' }}
                  multiline
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" className={button} type="submit" color="primary">
                  {submitLabel}
                </Button>
              </Grid>
            </Grid>
          </MuiPickersUtilsProvider>
        </Form>
      )}
    </Formik>
  );
};

export default TransferForm;
